/**
 * The state exam — egzamin certyfikatowy z języka polskiego jako obcego.
 *
 * Every number here is from the examining body's own structure table
 * (certyfikatpolski.pl, "Struktura egzaminu"), read on 8 September 2026. It is
 * the only place these rules are written down; views and scoring read from
 * here, and nothing copies a number out.
 *
 * Three things separate it from telc, and all three are why `ExamVariant`
 * exists at all:
 *
 *  - a candidate registers for one level and sits that level's paper. There is
 *    no single sitting that comes back B1 or B2 depending on the score.
 *  - the pass mark is a share of the points, not an absolute number, because
 *    the point totals differ from sitting to sitting. Each set therefore
 *    carries its own maxima and the threshold is applied to them.
 *  - from B1 up, every part has to clear the bar on its own. A strong reading
 *    score cannot carry a weak grammar score, which is the single most useful
 *    thing to tell a candidate before they pay for the exam.
 *
 * The structure table gives only a total for the written paper ("nie dłużej
 * niż 190 minut"), but every paper prints a time over each block it times:
 * "CZAS: 40 minut" over reading, 45 over grammar, 75 over writing at B1; 60,
 * 60 and 90 at B2. They were the same on all 24 adult papers in `.sources/`
 * (read 3 October 2026), so the adult B1 and B2 variants carry them, and
 * `tests/certyfikat-minutes.test.ts` re-reads the PDFs wherever they are on
 * disk. Listening has no printed time: its clock is the sitting's recording
 * (`audioSeconds`). The variants with no paper in the library keep `null`.
 */
import type { ExamVariant, ModuleSpec } from "./exam-family.ts";

const SOURCE = "certyfikatpolski.pl, Struktura egzaminu (read 8 September 2026)";

const listening: ModuleSpec = {
  id: "listening",
  name: "Rozumienie ze słuchu",
  minutes: null,
  items: null,
};
const reading: ModuleSpec = {
  id: "reading",
  name: "Rozumienie tekstów pisanych",
  minutes: null,
  items: null,
};
/** A2 and B1 for children fold grammar into the reading part rather than testing it apart. */
const readingWithGrammar: ModuleSpec = {
  id: "reading",
  name: "Rozumienie tekstów pisanych z rozpoznawaniem struktur gramatycznych",
  minutes: null,
  items: null,
};
const grammar: ModuleSpec = {
  id: "elements",
  name: "Poprawność gramatyczna",
  minutes: null,
  items: null,
};
const writing: ModuleSpec = {
  id: "writing",
  name: "Wypowiedź pisemna",
  minutes: null,
  items: 0,
};

const FULL = [listening, reading, grammar, writing];

/** A level's paper, with the minutes it prints over each block. */
const timed = (minutes: { reading: number; elements: number; writing: number }): ModuleSpec[] => [
  listening,
  { ...reading, minutes: minutes.reading },
  { ...grammar, minutes: minutes.elements },
  { ...writing, minutes: minutes.writing },
];

/**
 * The writing block's criteria, as every adult paper of the level prints them
 * under "KRYTERIA OCENY": task (content, form, length), range of language and
 * accuracy, each 0 to the figure here, for the block — both texts together.
 * What the paper does not print is what each point stands for, nor how the
 * block's points split between the two texts; those stay with the examiners.
 */
export const PAPER_WRITING_CRITERIA = {
  B1: { task: 10, range: 10, accuracy: 10 },
  B2: { task: 10, range: 16, accuracy: 14 },
} as const;

/** Printed on every adult paper of the level ("CZAS: … minut"). */
export const PAPER_BLOCK_MINUTES = {
  B1: { reading: 40, elements: 45, writing: 75 },
  B2: { reading: 60, elements: 60, writing: 90 },
} as const;
const NO_GRAMMAR = [listening, readingWithGrammar, writing];

/**
 * Only the levels the body currently examines. A2 up for adults, B1 and B2 for
 * children and adolescents — a variant for a level nobody can sit would be a
 * page promising an exam that does not run.
 */
export const CERTYFIKAT_VARIANTS: ExamVariant[] = [
  {
    id: "certyfikat.a2.adults",
    family: "certyfikat",
    levels: ["A2"],
    group: "adults",
    modules: NO_GRAMMAR,
    writtenMinutes: 160,
    oralMinutes: 10,
    pass: { kind: "writtenAndOral", ratio: 0.5 },
    source: SOURCE,
  },
  {
    id: "certyfikat.b1.adults",
    family: "certyfikat",
    levels: ["B1"],
    group: "adults",
    modules: timed(PAPER_BLOCK_MINUTES.B1),
    writtenMinutes: 190,
    oralMinutes: 15,
    pass: { kind: "perPart", ratio: 0.5 },
    source: SOURCE,
  },
  {
    id: "certyfikat.b2.adults",
    family: "certyfikat",
    levels: ["B2"],
    group: "adults",
    modules: timed(PAPER_BLOCK_MINUTES.B2),
    writtenMinutes: 250,
    oralMinutes: 15,
    pass: { kind: "perPart", ratio: 0.6 },
    source: SOURCE,
  },
  {
    id: "certyfikat.c1.adults",
    family: "certyfikat",
    levels: ["C1"],
    group: "adults",
    modules: FULL,
    writtenMinutes: 250,
    oralMinutes: 20,
    pass: { kind: "perPart", ratio: 0.6 },
    source: SOURCE,
  },
  {
    id: "certyfikat.c2.adults",
    family: "certyfikat",
    levels: ["C2"],
    group: "adults",
    modules: FULL,
    writtenMinutes: 290,
    oralMinutes: 30,
    pass: { kind: "perPart", ratio: 0.6 },
    source: SOURCE,
  },
  {
    id: "certyfikat.b1.young",
    family: "certyfikat",
    levels: ["B1"],
    group: "young",
    modules: NO_GRAMMAR,
    writtenMinutes: 190,
    oralMinutes: 15,
    pass: { kind: "writtenAndOral", ratio: 0.5 },
    source: SOURCE,
  },
  {
    id: "certyfikat.b2.young",
    family: "certyfikat",
    levels: ["B2"],
    group: "young",
    modules: FULL,
    writtenMinutes: 250,
    oralMinutes: 15,
    pass: { kind: "writtenAndOral", ratio: 0.5 },
    source: SOURCE,
  },
];

/**
 * The certificate a B1 paper leads to is the one the citizenship application
 * asks for, and telc's is not accepted there. That asymmetry is the reason this
 * exam is in the app at all, so it is recorded next to the exam rather than
 * only in the marketing copy.
 */
export const CITIZENSHIP_VARIANT_ID = "certyfikat.b1.adults";

/**
 * What a candidate can read off the paper at each adult level, beyond the
 * structure table: how many points each part is worth, and how many words the
 * writing block asks for.
 *
 * The structure table gives a share and a time; the points and the word counts
 * are printed on the papers themselves, and they have been the same on every
 * paper of a level in the library. They are written down here, once, so a page
 * can show a level's paper without the number living in its copy, and
 * `tests/certyfikat-levels.test.ts` counts the real papers and fails the day a
 * sitting prints something else.
 *
 * The writing block is two texts (`shortWords`, `longWords`) and each brief
 * prints its own length, so these are the range across the library, not a rule.
 */
export const CERTYFIKAT_LEVEL_FACTS = {
  B1: {
    variant: "certyfikat.b1.adults",
    points: { listening: 30, reading: 30, elements: 30, writing: 30, speaking: 40 },
    shortWords: { min: 25, max: 60 },
    longWords: { min: 140, max: 175 },
  },
  B2: {
    variant: "certyfikat.b2.adults",
    points: { listening: 40, reading: 40, elements: 40, writing: 40, speaking: 40 },
    shortWords: { min: 30, max: 150 },
    longWords: { min: 150, max: 270 },
  },
} as const;

/** The structure-table row for one adult level. */
export const certyfikatVariant = (level: "B1" | "B2"): ExamVariant =>
  CERTYFIKAT_VARIANTS.find((v) => v.id === CERTYFIKAT_LEVEL_FACTS[level].variant)!;

/**
 * How many tasks each test block of an adult paper has, and which forms of task
 * it draws on.
 *
 * The count is the same on every paper of a level in the library; the forms move
 * around from sitting to sitting, so what is written down is the set that occurs,
 * not an order. `tests/exam-tasks.test.ts` holds both against the real papers.
 * The forms are the task kinds of `schema.ts`, sorted.
 */
export const CERTYFIKAT_TASK_FACTS = {
  B1: {
    listening: {
      tasks: 5,
      kinds: ["bankGaps", "groupedMatch", "listeningQuestions", "trueFalse"],
    },
    reading: {
      tasks: 5,
      kinds: ["bankGaps", "inlineChoice", "texts", "trueFalse"],
    },
    elements: { tasks: 8, kinds: ["bankGaps", "gapWrite", "inlineChoice"] },
  },
  B2: {
    listening: {
      tasks: 5,
      kinds: ["bankGaps", "gapWrite", "groupedMatch", "listeningQuestions", "trueFalse"],
    },
    reading: {
      tasks: 5,
      kinds: ["bankGaps", "groupedMatch", "inlineChoice", "texts", "trueFalse"],
    },
    elements: { tasks: 8, kinds: ["bankGaps", "gapWrite", "inlineChoice"] },
  },
} as const;
